import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Tag, Button, message, Spin, Empty, Statistic, Space, Divider, Alert } from 'antd';
import { GiftOutlined, CopyOutlined, PercentageOutlined, DollarOutlined, ShoppingCartOutlined, CalendarOutlined, CheckCircleFilled, MinusCircleOutlined } from '@ant-design/icons';
import { couponService } from '../services/api';
import './CustomerDashboard.css';

const { Title, Text, Paragraph } = Typography;

const Coupons = () => {
  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [userCoupons, setUserCoupons] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [allRes, userRes] = await Promise.all([
        couponService.getCoupons().catch(() => ({ data: [] })),
        couponService.getUserCoupons().catch(() => ({ data: [] })),
      ]);
      const allData = allRes.data?.data || allRes.data || [];
      const userData = userRes.data?.data || userRes.data || [];
      setCoupons(Array.isArray(allData) ? allData : []);
      setUserCoupons(Array.isArray(userData) ? userData : []);
    } catch (err) {
      console.error('Failed to fetch coupons:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    message.success(`Coupon code "${code}" copied!`);
  };

  if (loading) return <div style={{ textAlign: 'center', paddingTop: 80 }}><Spin size="large" /></div>;

  const allCoupons = [...coupons, ...userCoupons.filter(uc => uc.coupon).map(uc => uc.coupon)];
  const uniqueCoupons = allCoupons.filter((c, i, arr) => c?.code && arr.findIndex(x => x?.code === c.code) === i);

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div className="customer-page-header">
        <Title level={2}>Coupons & Offers</Title>
        <Paragraph>Discover discounts and promotions to save on your orders.</Paragraph>
      </div>

      {userCoupons.length > 0 && (
        <Alert message={`You have ${userCoupons.length} saved coupon(s)`} type="success" showIcon style={{ marginBottom: 16 }} />
      )}

      {uniqueCoupons.length === 0 ? (
        <Card><Empty description="No coupons available right now. Check back later!" /></Card>
      ) : (
        <Row gutter={[16, 16]}>
          {uniqueCoupons.map((coupon) => {
            const isPercentage = coupon.discount_type === 'percentage';
            const discountLabel = isPercentage ? `${coupon.discount_value}% OFF` : `NPR ${coupon.discount_value} OFF`;
            const isValid = coupon.is_active !== false && (!coupon.valid_until || new Date(coupon.valid_until) > new Date());
            const isExpired = coupon.valid_until && new Date(coupon.valid_until) < new Date();

            return (
              <Col xs={24} sm={12} lg={8} key={coupon.id || coupon.code}>
                <Card className="customer-card" style={{ opacity: isExpired ? 0.5 : 1, border: isValid && !isExpired ? '2px solid #52b788' : '1px solid #e5e7eb', position: 'relative', overflow: 'hidden' }}>
                  {isExpired && <Tag color="default" style={{ position: 'absolute', top: 8, right: 8 }}>Expired</Tag>}
                  {isValid && !isExpired && <Tag color="green" style={{ position: 'absolute', top: 8, right: 8 }}>Active</Tag>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #52b788, #2d5a3d)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <GiftOutlined style={{ fontSize: 22, color: '#fff' }} />
                    </div>
                    <div>
                      <Title level={5} style={{ margin: 0, color: '#2d5a3d' }}>{coupon.code}</Title>
                      <Tag color={isPercentage ? 'blue' : 'green'}>{discountLabel}</Tag>
                    </div>
                  </div>
                  {coupon.description && <Paragraph style={{ color: '#6b7280', fontSize: 13, marginBottom: 8 }}>{coupon.description}</Paragraph>}
                  <Divider style={{ margin: '8px 0' }} />
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>
                    {coupon.min_order_amount > 0 && <div><DollarOutlined /> Min. order: NPR {coupon.min_order_amount}</div>}
                    {coupon.valid_until && <div><CalendarOutlined /> Valid until: {new Date(coupon.valid_until).toLocaleDateString()}</div>}
                    {coupon.usage_limit > 0 && <div><ShoppingCartOutlined /> {coupon.used_count || 0}/{coupon.usage_limit} used</div>}
                  </div>
                  {!isExpired && (
                    <Button type="primary" block icon={<CopyOutlined />} onClick={() => handleCopyCode(coupon.code)}
                      style={{ marginTop: 12, borderRadius: 8, background: 'linear-gradient(135deg, #52b788, #2d5a3d)', border: 'none' }}>
                      Copy Code
                    </Button>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
};

export default Coupons;
